/**
 * Document Type Detector
 * Identifies whether a PDF is DS-160, I-129, LCA, or unknown
 */

import type { VisaDocumentType, DocumentDetectionResult } from './types';

// Pattern matchers for each document type
interface PatternMatcher {
  patterns: RegExp[];
  weight: number; // Higher weight = more indicative
}

const DS160_PATTERNS: PatternMatcher[] = [
  // Confirmation page indicators
  { patterns: [/DS-160/i], weight: 10 },
  { patterns: [/Nonimmigrant Visa Application/i], weight: 8 },
  { patterns: [/Confirmation Page/i, /Application Confirmation/i], weight: 7 },
  { patterns: [/Consular Section/i, /U\.?S\.? Embassy/i, /U\.?S\.? Consulate/i], weight: 5 },

  // Form sections
  { patterns: [/Personal Information 1/i, /Personal Information 2/i], weight: 6 },
  { patterns: [/Travel Information/i], weight: 4 },
  { patterns: [/Address and Phone Information/i], weight: 5 },
  { patterns: [/Passport Information/i], weight: 4 },
  { patterns: [/U\.?S\.? Contact Information/i], weight: 5 },
  { patterns: [/Family Information/i], weight: 3 },
  { patterns: [/Work\/Education\/Training/i, /Present Work\/Education/i], weight: 5 },
  { patterns: [/Previous U\.?S\.? Travel/i], weight: 6 },
  { patterns: [/Security and Background/i], weight: 5 },

  // Specific fields
  { patterns: [/Application ID/i, /Confirmation Number/i], weight: 4 },
  { patterns: [/Barcode/i, /AA\d{10}/i], weight: 6 }, // DS-160 confirmation numbers
  { patterns: [/CEAC/i], weight: 8 }, // Consular Electronic Application Center
  { patterns: [/Social Media/i, /Social Media Identifier/i], weight: 4 },
];

const I129_PATTERNS: PatternMatcher[] = [
  // Form identifiers
  { patterns: [/I-129/i, /Form I-129/i], weight: 10 },
  { patterns: [/Petition for (?:a )?Nonimmigrant Worker/i], weight: 9 },
  { patterns: [/USCIS/i, /U\.?S\.? Citizenship and Immigration Services/i], weight: 5 },

  // H-1B specific
  { patterns: [/H-1B/i], weight: 8 },
  { patterns: [/H-1B Data Collection/i, /H-1B Supplement/i], weight: 9 },
  { patterns: [/Specialty Occupation/i], weight: 7 },
  { patterns: [/LCA Number/i, /Labor Condition Application/i], weight: 6 },

  // L-1 specific
  { patterns: [/L-1A/i, /L-1B/i], weight: 8 },
  { patterns: [/Intracompany Transferee/i], weight: 7 },

  // Form sections
  { patterns: [/Part 1\.\s*(?:Information About the Petitioner|Petitioner)/i], weight: 6 },
  { patterns: [/Part 2\.\s*(?:Information About This Petition|Requested)/i], weight: 6 },
  { patterns: [/Part 3\.\s*(?:Information About the Beneficiary|Beneficiary)/i], weight: 6 },
  { patterns: [/Part 4\.\s*(?:Processing Information)/i], weight: 5 },
  { patterns: [/Part 5\.\s*(?:Basic Information About the Proposed Employment)/i], weight: 6 },

  // Specific fields
  { patterns: [/Employer Identification Number/i, /EIN/i], weight: 4 },
  { patterns: [/Beneficiary.*Name/i], weight: 5 },
  { patterns: [/Petitioner.*Name/i, /Sponsoring.*Employer/i], weight: 5 },
  { patterns: [/Receipt Number/i, /WAC|EAC|LIN|SRC|MSC|IOE/i], weight: 7 },
  { patterns: [/Premium Processing/i], weight: 5 },
  { patterns: [/SOC Code/i, /Standard Occupational Classification/i], weight: 6 },
  { patterns: [/Prevailing Wage/i], weight: 5 },
];

const LCA_PATTERNS: PatternMatcher[] = [
  // Form identifiers
  { patterns: [/ETA Form 9035/i, /Form 9035/i, /ETA-9035/i], weight: 10 },
  { patterns: [/Labor Condition Application/i], weight: 9 },
  { patterns: [/LCA/i], weight: 7 },
  { patterns: [/Department of Labor/i, /DOL/i], weight: 5 },

  // Case information
  { patterns: [/Case Number/i, /I-\d{3}-\d{5}-\d{7}/i], weight: 8 },
  { patterns: [/Case Status/i, /Certified/i, /Denied/i, /Withdrawn/i], weight: 5 },

  // Wage information
  { patterns: [/Prevailing Wage/i, /Actual Wage/i], weight: 6 },
  { patterns: [/Wage Rate/i, /Rate of Pay/i], weight: 5 },
  { patterns: [/Level I|Level II|Level III|Level IV/i], weight: 6 },
  { patterns: [/OES/i, /OFLC/i], weight: 7 }, // Wage sources

  // Job details
  { patterns: [/SOC Code/i, /SOC Title/i], weight: 6 },
  { patterns: [/Primary Worksite/i, /Worksite Address/i], weight: 5 },
  { patterns: [/Total Workers/i, /Number of Workers/i], weight: 5 },

  // Employer attestations
  { patterns: [/Employer Attestation/i], weight: 6 },
  { patterns: [/H-1B Dependent/i, /Willful Violator/i], weight: 7 },
  { patterns: [/Public Disclosure/i], weight: 5 },
];

/**
 * Calculate match score for a set of patterns
 */
function calculateScore(text: string, patterns: PatternMatcher[]): { score: number; matches: string[] } {
  let score = 0;
  const matches: string[] = [];

  for (const matcher of patterns) {
    for (const pattern of matcher.patterns) {
      if (pattern.test(text)) {
        score += matcher.weight;
        matches.push(pattern.source);
        break; // Only count each pattern group once
      }
    }
  }

  return { score, matches };
}

/**
 * Detect the type of visa document from extracted text
 */
export function detectDocumentType(text: string): DocumentDetectionResult {
  const normalizedText = text.replace(/\s+/g, ' ');

  const ds160Result = calculateScore(normalizedText, DS160_PATTERNS);
  const i129Result = calculateScore(normalizedText, I129_PATTERNS);
  const lcaResult = calculateScore(normalizedText, LCA_PATTERNS);

  // Find the highest scoring type
  const scores: { type: VisaDocumentType; score: number; matches: string[] }[] = [
    { type: 'DS-160', ...ds160Result },
    { type: 'I-129', ...i129Result },
    { type: 'LCA', ...lcaResult },
  ];

  scores.sort((a, b) => b.score - a.score);

  const winner = scores[0];
  const runnerUp = scores[1];

  // Calculate confidence based on score difference and absolute score
  // Minimum score threshold for detection
  const MIN_SCORE = 15;
  const CONFIDENCE_THRESHOLD = 0.3;

  if (winner.score < MIN_SCORE) {
    return {
      type: 'unknown',
      confidence: 0,
      indicators: [],
    };
  }

  // Calculate confidence as ratio of winner to max possible + margin over runner-up
  const maxPossibleScore = winner.type === 'DS-160'
    ? DS160_PATTERNS.reduce((sum, p) => sum + p.weight, 0)
    : winner.type === 'I-129'
    ? I129_PATTERNS.reduce((sum, p) => sum + p.weight, 0)
    : LCA_PATTERNS.reduce((sum, p) => sum + p.weight, 0);

  const absoluteConfidence = winner.score / maxPossibleScore;
  const relativeConfidence = runnerUp.score > 0
    ? (winner.score - runnerUp.score) / winner.score
    : 1;

  const confidence = Math.min(1, (absoluteConfidence + relativeConfidence) / 2);

  if (confidence < CONFIDENCE_THRESHOLD) {
    return {
      type: 'unknown',
      confidence,
      indicators: winner.matches,
    };
  }

  return {
    type: winner.type,
    confidence,
    indicators: winner.matches,
  };
}

/**
 * Get human-readable document name
 */
export function getDocumentName(type: VisaDocumentType): string {
  switch (type) {
    case 'DS-160':
      return 'DS-160 (Nonimmigrant Visa Application)';
    case 'I-129':
      return 'I-129 (Petition for Nonimmigrant Worker)';
    case 'LCA':
      return 'LCA (Labor Condition Application)';
    default:
      return 'Unknown Document Type';
  }
}

/**
 * Get confidence level description
 */
export function getConfidenceLevel(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.7) return 'high';
  if (confidence >= 0.4) return 'medium';
  return 'low';
}
